import { useState } from "react";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./AreasCarousel.scss";

const ICONS_BASE =
  "https://essentialnutrition-upload-files.s3.us-east-1.amazonaws.com/site-essentiagroup/carreiras";

// 12 cards — preencha título e texto conforme necessário.
const AREAS = [
  { id: 1, key: "laboratory" },
  { id: 2, key: "it" },
  { id: 3, key: "customerService" },
  { id: 4, key: "finance" },
  { id: 5, key: "administrative" },
  { id: 6, key: "commercial" },
  { id: 7, key: "projects" },
  { id: 8, key: "production" },
  { id: 9, key: "software" },
  { id: 10, key: "marketing" },
  { id: 11, key: "businessPerformance" },
  { id: 12, key: "peopleDevelopment" },
];

const iconUrl = (id) =>
  `${ICONS_BASE}/icon-${String(id).padStart(2, "0")}.svg`;

// onSlideChange notifica o pai para sincronizar os dots externos.
// onEndChange informa quando não há mais cards à direita.
// sliderRef é passado pelo pai para permitir navegação programática (goTo via dots).
const AreasCarousel = ({
  onSlideChange,
  onSliderInfoChange,
  onEndChange,
  sliderRef,
}) => {
  const { t } = useTranslation();

  // Estado local usado apenas para desabilitar a seta ao chegar no fim.
  const [isAtEnd, setIsAtEnd] = useState(false);

  const getSlidesToShow = () => {
    // O Slick pode manter o valor padrão no estado interno durante o reinit.
    // O breakpoint da viewport é a fonte estável para a paginação.
    if (window.matchMedia("(max-width: 767px)").matches) return 1;
    if (window.matchMedia("(max-width: 1100px)").matches) return 2;
    return 3;
  };

  const syncSliderInfo = () => {
    const slidesToShow = getSlidesToShow();
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const slidesToScroll =
      sliderRef?.current?.innerSlider?.state?.slidesToScroll || 1;
    const dotsTotal = isMobile
      ? AREAS.length
      : Math.floor((AREAS.length - slidesToShow) / slidesToScroll) + 1;

    onSliderInfoChange?.({ dotsTotal, dotsStep: 1 });
    return slidesToShow;
  };

  const handleSlideChange = (next) => {
    onSlideChange?.(next);

    const slidesToShow = syncSliderInfo();
    const atEnd = next + slidesToShow >= AREAS.length;
    setIsAtEnd(atEnd);
    onEndChange?.(atEnd);
  };

  const settings = {
    dots: false,            // dots gerenciados externamente
    arrows: false,
    draggable: true,
    swipe: true,
    infinite: false,
    speed: 400,
    variableWidth: true,
    slidesToShow: window.matchMedia("(max-width: 767px)").matches
      ? 1
      : window.matchMedia("(max-width: 1100px)").matches
      ? 2
      : 3,
    slidesToScroll: 1,
    onInit: syncSliderInfo,
    onReInit: syncSliderInfo,
    beforeChange: (_, next) => handleSlideChange(next),
    responsive: [
      { breakpoint: 1101, settings: { slidesToShow: 2 } },
      { breakpoint: 767,  settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  return (
    <div
      className="areas-carousel"
      role="region"
      aria-roledescription="carrossel"
      aria-label={t("careers.areas.carouselLabel")}
    >
      <Slider ref={sliderRef} {...settings}>
        {AREAS.map((area) => (
          <div key={area.id} className="areas-carousel__slide">
            <article
              className="areas-card"
              aria-roledescription="slide"
              aria-label={`${t(`careers.areas.items.${area.key}.title`)}: ${t(
                `careers.areas.items.${area.key}.text`
              )}`}
            >
              <img
                className="areas-card__icon"
                src={iconUrl(area.id)}
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              <h3 className="areas-card__title">
                {t(`careers.areas.items.${area.key}.title`)}
              </h3>
              <p className="areas-card__text">
                {t(`careers.areas.items.${area.key}.text`)}
              </p>
            </article>
          </div>
        ))}
      </Slider>

      {/* Seta de navegação sobreposta ao carrossel (apenas direita).
          Fica oculta no fim para não confundir o usuário. */}
      <button
        type="button"
        className="areas-carousel__arrow areas-carousel__arrow--next"
        aria-label={t("careers.areas.next")}
        onClick={() => sliderRef?.current?.slickNext()}
        disabled={isAtEnd}
      >
        <ArrowIcon direction="right" />
      </button>
    </div>
  );
};

// Seta branca em SVG inline: acompanha a cor via currentColor e não faz
// requisição de rede. direction controla o sentido (left/right).
const ArrowIcon = ({ direction }) => (
  <svg
    className={`areas-carousel__arrow-icon areas-carousel__arrow-icon--${direction}`}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 12h16m0 0-6-6m6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Número de dots visíveis na paginação.
export const AREAS_TOTAL = AREAS.length;

export default AreasCarousel;
